import { useState, useEffect, useCallback } from 'react';
import {
  diagnoseAd,
  type AdDiagnoseRequest,
  type AdDiagnoseResponse,
  type DiagnoseIssue,
  type DiagnoseSuggestion,
  type DiagnoseVideoScene,
} from '../api/ai';

// ── Color helpers ────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--green)';
  if (score >= 60) return 'var(--amber)';
  return 'var(--red)';
}

function scoreBg(score: number): string {
  if (score >= 80) return 'var(--green-bg)';
  if (score >= 60) return 'var(--amber-bg)';
  return 'var(--red-bg)';
}

function severityColor(s: string): string {
  switch (s) {
    case 'critical': return 'var(--red)';
    case 'warning': return 'var(--amber)';
    case 'good': return 'var(--green)';
    case 'info': return '#3B82F6';
    default: return 'var(--text3)';
  }
}

function severityBg(s: string): string {
  switch (s) {
    case 'critical': return 'var(--red-bg)';
    case 'warning': return 'var(--amber-bg)';
    case 'good': return 'var(--green-bg)';
    case 'info': return 'rgba(59,130,246,.08)';
    default: return 'var(--surface2)';
  }
}

function severityBorder(s: string): string {
  switch (s) {
    case 'critical': return 'rgba(239,68,68,.25)';
    case 'warning': return 'rgba(245,158,11,.25)';
    case 'good': return 'rgba(16,185,129,.25)';
    case 'info': return 'rgba(59,130,246,.25)';
    default: return 'var(--border)';
  }
}

function tagTypeColor(t: string): string {
  switch (t) {
    case 'green': return 'var(--green)';
    case 'amber': return 'var(--amber)';
    case 'red': return 'var(--red)';
    case 'blue': return '#3B82F6';
    default: return 'var(--text3)';
  }
}

function tagTypeBg(t: string): string {
  switch (t) {
    case 'green': return 'var(--green-bg)';
    case 'amber': return 'var(--amber-bg)';
    case 'red': return 'var(--red-bg)';
    case 'blue': return 'rgba(59,130,246,.08)';
    default: return 'var(--surface2)';
  }
}

// ── Ad type default icons ───────────────────────────────────────────────────

function AdTypeIcon({ type, size = 48 }: { type: 'video' | 'image' | 'text'; size?: number }) {
  const r = size * 0.16;
  if (type === 'video') {
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ borderRadius: r, flexShrink: 0 }}>
        <defs>
          <linearGradient id="adicon-vid" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4A1E0F" /><stop offset="1" stopColor="#993C1D" />
          </linearGradient>
        </defs>
        <rect width="48" height="48" rx={r} fill="url(#adicon-vid)" />
        <rect x="11" y="15" width="18" height="18" rx="3" stroke="#E8A87C" strokeWidth="1.6" strokeOpacity=".6" fill="none" />
        <polygon points="35,20 35,28 29,24" fill="#E8A87C" fillOpacity=".7" />
        <circle cx="16" cy="36" r="5" fill="#993C1D" fillOpacity=".3" />
      </svg>
    );
  }
  if (type === 'image') {
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ borderRadius: r, flexShrink: 0 }}>
        <defs>
          <linearGradient id="adicon-img" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0C3A6B" /><stop offset="1" stopColor="#185FA5" />
          </linearGradient>
        </defs>
        <rect width="48" height="48" rx={r} fill="url(#adicon-img)" />
        <rect x="11" y="12" width="26" height="24" rx="3" stroke="#7DB8F0" strokeWidth="1.6" strokeOpacity=".5" fill="none" />
        <circle cx="18" cy="20" r="3" fill="#7DB8F0" fillOpacity=".5" />
        <polyline points="11,32 19,24 26,30 31,26 37,32" stroke="#7DB8F0" strokeWidth="1.6" strokeOpacity=".6" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }
  // text
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ borderRadius: r, flexShrink: 0 }}>
      <defs>
        <linearGradient id="adicon-txt" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2E2470" /><stop offset="1" stopColor="#534AB7" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx={r} fill="url(#adicon-txt)" />
      <rect x="12" y="11" width="24" height="26" rx="3" stroke="#A8A0E0" strokeWidth="1.6" strokeOpacity=".5" fill="none" />
      <line x1="17" y1="19" x2="31" y2="19" stroke="#A8A0E0" strokeWidth="1.4" strokeOpacity=".6" strokeLinecap="round" />
      <line x1="17" y1="24" x2="28" y2="24" stroke="#A8A0E0" strokeWidth="1.4" strokeOpacity=".45" strokeLinecap="round" />
      <line x1="17" y1="29" x2="24" y2="29" stroke="#A8A0E0" strokeWidth="1.4" strokeOpacity=".35" strokeLinecap="round" />
    </svg>
  );
}

function AdThumb({ url, adType, name }: { url?: string; adType: 'video' | 'image' | 'text'; name: string }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <AdTypeIcon type={adType} size={48} />;
  return (
    <img
      src={url}
      alt={name}
      style={{ width: 48, height: 48, borderRadius: 8, flexShrink: 0, objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  );
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface AdDiagnosisModalProps {
  open: boolean;
  onClose: () => void;
  ad: {
    id: number;
    third_ad_id: string;
    name: string;
    image_url?: string;
    video_url?: string;
    platform?: string;
    headline?: string;
    primary_text?: string;
    description?: string;
    cta_type?: string;
  } | null;
  metrics: {
    spend: number;
    roas: number;
    cpc: number;
    cpm: number;
    cpa: number;
    purchase_count: number;
  } | null;
}

// ── Component ────────────────────────────────────────────────────────────────

export function AdDiagnosisModal({ open, onClose, ad, metrics }: AdDiagnosisModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdDiagnoseResponse | null>(null);

  const buildRequest = useCallback((): AdDiagnoseRequest | null => {
    if (!ad || !metrics) return null;
    let adType: 'text' | 'image' | 'video' = 'text';
    if (ad.video_url) adType = 'video';
    else if (ad.image_url) adType = 'image';
    return {
      ad_id: ad.third_ad_id,
      ad_name: ad.name || ad.third_ad_id,
      ad_type: adType,
      platform: ad.platform || 'facebook',
      headline: ad.headline || undefined,
      primary_text: ad.primary_text || undefined,
      description: ad.description || undefined,
      cta: ad.cta_type || undefined,
      metrics: {
        spend: metrics.spend,
        roas: metrics.roas,
        cpc: metrics.cpc,
        cpm: metrics.cpm,
        cpa: metrics.cpa,
        purchase_count: metrics.purchase_count,
      },
      image_url: ad.image_url || undefined,
      video_url: ad.video_url || undefined,
    };
  }, [ad, metrics]);

  const fetchDiagnosis = useCallback(async (regenerate = false) => {
    const req = buildRequest();
    if (!req) return;

    setLoading(true);
    setError(null);
    if (regenerate) setData(null);

    try {
      // If not regenerating, try cached first via POST (backend checks DB)
      const resp = await diagnoseAd({ ...req, regenerate });
      setData(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI 分析失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [buildRequest]);

  useEffect(() => {
    if (open && ad && metrics) {
      fetchDiagnosis(false);
    }
    if (!open) {
      setData(null);
      setError(null);
}
  }, [open, ad, metrics, fetchDiagnosis]);

  if (!open || !ad) return null;

  const adType = ad.video_url ? 'video' : ad.image_url ? 'image' : 'text';
  const adTypeLabel = adType === 'video' ? '视频广告' : adType === 'image' ? '图片广告' : '文本广告';

  const fmtMoney = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="modal-overlay open" onClick={onClose}>
      <div
        className="modal modal-lg"
        style={{ maxWidth: 760, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              <h3 style={{ margin: 0 }}>AI 广告诊断</h3>
              {/* <span style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 4,
                background: 'var(--primary-bg)', color: 'var(--primary)', fontWeight: 500,
              }}>
                Powered by 鹿鸣
              </span> */}
            </div>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          {/* Ad info bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', background: 'var(--surface2)', borderRadius: 10,
          }}>
            <AdThumb url={ad.image_url || ad.video_url} adType={adType as 'video' | 'image' | 'text'} name={ad.name} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 14, fontWeight: 500,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {ad.name || ad.third_ad_id}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {ad.third_ad_id} · {adTypeLabel}
              </div>
            </div>
            {metrics && (
              <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{fmtMoney(metrics.spend)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>花费</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 15, fontWeight: 600,
                    color: metrics.roas >= 3 ? 'var(--green)' : metrics.roas > 0 ? 'var(--text)' : 'var(--text3)',
                  }}>
                    {metrics.roas.toFixed(2)}x
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>ROAS</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{fmtMoney(metrics.cpc)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>CPC</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ padding: '24px 28px' }}>
          {loading && <LoadingState />}
          {error && <ErrorState error={error} onRetry={fetchDiagnosis} />}
          {data && <DiagnosisContent data={data} adType={adType} />}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>关闭</button>
          {data && (
            <>
              <button
                className="btn btn-outline"
                disabled={loading}
                onClick={() => fetchDiagnosis(true)}
              >
                {loading ? '生成中...' : '重新生成'}
              </button>
              <button
                className="btn"
                style={{ background: 'var(--primary)', color: '#fff' }}
                onClick={() => {/* future: adopt suggestions */}}
              >
                采纳建议并优化
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Loading state ────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 20px', gap: 16,
    }}>
      <div className="ad-diag-spinner" />
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
        AI 正在分析广告数据...
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)' }}>
        预计需要 10-20 秒，请稍候
      </div>
    </div>
  );
}

// ── Error state ──────────────────────────────────────────────────────────────

function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 20px', gap: 12,
    }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>分析失败</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>{error}</div>
      <button
        className="btn btn-outline"
        style={{ marginTop: 8 }}
        onClick={onRetry}
      >
        重新分析
      </button>
    </div>
  );
}

// ── Main diagnosis content ───────────────────────────────────────────────────

function DiagnosisContent({ data, adType }: { data: AdDiagnoseResponse; adType: string }) {
  const dimAvg = data.dimensions.length > 0
    ? Math.round(data.dimensions.reduce((s, d) => s + d.score, 0) / data.dimensions.length)
    : data.score;
  const finalScore = dimAvg > 0 ? dimAvg : data.score;
  const sc = scoreColor(finalScore);
  const circumference = 2 * Math.PI * 48;
  const offset = circumference - (finalScore / 100) * circumference;

  const dimLabel = adType === 'video' ? '视频评分维度'
    : adType === 'image' ? '图片评分维度'
    : '文案评分维度';

  return (
    <>
      {/* Score + Dimensions */}
      <div className="ad-diag-score-row">
        <div className="ad-diag-score-ring-wrap">
          <div className="ad-diag-ring">
            <svg viewBox="0 0 110 110" width="110" height="110">
              <circle cx="55" cy="55" r="48" fill="none" stroke="var(--surface2)" strokeWidth="8" />
              <circle
                cx="55" cy="55" r="48" fill="none"
                stroke={sc} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '55px 55px', transition: 'stroke-dashoffset .6s ease' }}
              />
            </svg>
            <div className="ad-diag-ring-number" style={{ color: sc }}>
              {finalScore}
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>综合评分 / 100</div>
          <span style={{
            display: 'inline-block', fontSize: 11, padding: '3px 12px', borderRadius: 4,
            fontWeight: 500, marginTop: 6, background: scoreBg(finalScore), color: sc,
          }}>
            {data.grade}
          </span>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{dimLabel}</div>
          {data.dimensions.map((d, i) => {
            const dc = scoreColor(d.score);
            return (
              <div key={i} className="ad-diag-dim-row">
                <span className="ad-diag-dim-label">{d.label}</span>
                <div className="ad-diag-dim-track">
                  <div
                    className="ad-diag-dim-fill"
                    style={{ width: `${d.score}%`, background: dc, transition: 'width .5s ease' }}
                  />
                </div>
                <span className="ad-diag-dim-num" style={{ color: dc }}>{d.score}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Video timeline + scenes */}
      {adType === 'video' && data.video_scenes && data.video_scenes.length > 0 && (
        <VideoTimeline scenes={data.video_scenes} />
      )}

      {/* Issues */}
      <div className="ad-diag-section">
        <div className="ad-diag-section-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          诊断详情
        </div>
        {data.issues.map((issue, i) => (
          <IssueCard key={i} issue={issue} />
        ))}
      </div>

      {/* Suggestions */}
      <div className="ad-diag-section">
        <div className="ad-diag-section-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
          AI 优化建议
        </div>
        {data.suggestions.map((s, i) => (
          <SuggestionCard key={i} index={i + 1} suggestion={s} />
        ))}
      </div>

      {/* Rewrite comparison (text ads) */}
      {data.rewrite && (
        <div className="ad-diag-section">
          <div className="ad-diag-section-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            AI 改写对比
          </div>
          <div className="ad-diag-rewrite-grid">
            <div className="ad-diag-rewrite-card">
              <div className="ad-diag-rewrite-label" style={{ background: 'var(--surface2)', color: 'var(--text3)' }}>
                原始 ({data.rewrite.old_score}分)
              </div>
              <div className="ad-diag-rewrite-text" style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>
                {data.rewrite.old}
              </div>
            </div>
            <div className="ad-diag-rewrite-card" style={{ borderColor: 'var(--primary)' }}>
              <div className="ad-diag-rewrite-label" style={{ background: 'var(--primary-bg)', color: 'var(--primary)' }}>
                AI优化 ({data.rewrite.new_score}分)
              </div>
              <div className="ad-diag-rewrite-text" style={{ whiteSpace: 'pre-wrap' }}>
                {(() => {
                  const text = data.rewrite!.new;
                  const enMatch = text.match(/\[EN\]\s*([\s\S]*?)(?=\[中文\]|$)/);
                  const zhMatch = text.match(/\[中文\]\s*([\s\S]*?)$/);
                  if (!enMatch && !zhMatch) return text;
                  return (
                    <>
                      {enMatch && (
                        <div style={{ marginBottom: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-bg)', padding: '2px 6px', borderRadius: 3, marginBottom: 4, display: 'inline-block' }}>EN</span>
                          <div style={{ marginTop: 4 }}>{enMatch[1].trim()}</div>
                        </div>
                      )}
                      {zhMatch && (
                        <div>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', background: 'rgba(245,166,35,0.1)', padding: '2px 6px', borderRadius: 3, marginBottom: 4, display: 'inline-block' }}>中文</span>
                          <div style={{ marginTop: 4 }}>{zhMatch[1].trim()}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Issue card ───────────────────────────────────────────────────────────────

function IssueCard({ issue }: { issue: DiagnoseIssue }) {
  const color = severityColor(issue.severity);
  const bg = severityBg(issue.severity);
  const border = severityBorder(issue.severity);

  const iconSvg = issue.severity === 'good'
    ? <><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
    : issue.severity === 'critical'
    ? <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
    : issue.severity === 'info'
    ? <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>
    : <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>;

  return (
    <div className="ad-diag-issue" style={{ background: bg, borderColor: border }}>
      <div className="ad-diag-issue-icon" style={{ background: bg }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          {iconSvg}
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color, marginBottom: 3 }}>{issue.title}</div>
        <div style={{ fontSize: 12, lineHeight: 1.6, opacity: 0.8 }}>{issue.desc}</div>
        <span style={{
          display: 'inline-block', fontSize: 10, padding: '2px 8px', borderRadius: 3,
          marginTop: 6, fontWeight: 500, background: bg, color,
        }}>
          {issue.impact}
        </span>
      </div>
    </div>
  );
}

// ── Suggestion card ──────────────────────────────────────────────────────────

function SuggestionCard({ index, suggestion }: { index: number; suggestion: DiagnoseSuggestion }) {
  return (
    <div className="ad-diag-suggestion">
      <div className="ad-diag-suggestion-num">{index}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{suggestion.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{suggestion.desc}</div>
        <span style={{
          display: 'inline-block', fontSize: 10, padding: '2px 8px', borderRadius: 3,
          marginTop: 6, fontWeight: 500,
          background: tagTypeBg(suggestion.tag_type), color: tagTypeColor(suggestion.tag_type),
        }}>
          {suggestion.tag}
        </span>
      </div>
    </div>
  );
}

// ── Video timeline ───────────────────────────────────────────────────────────

function VideoTimeline({ scenes }: { scenes: DiagnoseVideoScene[] }) {
  return (
    <div className="ad-diag-section">
      <div className="ad-diag-section-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="2">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" />
        </svg>
        视频时间轴分析
      </div>

      {/* Timeline bar */}
      <div style={{
        background: 'var(--surface2)', borderRadius: 10, padding: 16, marginBottom: 16,
      }}>
        <div style={{
          height: 8, background: 'var(--surface3)', borderRadius: 4,
          position: 'relative', overflow: 'hidden', marginBottom: 12,
        }}>
          {scenes.map((scene, i) => {
            const widthPct = 100 / scenes.length;
            const color = scene.score >= 70 ? 'var(--green)' : scene.score >= 45 ? 'var(--amber)' : 'var(--red)';
            return (
              <div
                key={i}
                style={{
                  position: 'absolute', top: 0, height: '100%',
                  left: `${i * widthPct}%`, width: `${widthPct}%`,
                  background: color, opacity: 0.5,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Scene cards */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>逐段诊断</div>
      {scenes.map((scene, i) => {
        const color = scoreColor(scene.score);
        return (
          <div key={i} className="ad-diag-scene">
            <div className="ad-diag-scene-time">{scene.time}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{scene.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>{scene.desc}</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: 'right', color }}>
              {scene.score}
            </div>
          </div>
        );
      })}
    </div>
  );
}
